import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Group,
  Image,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconRefresh, IconTrash } from "@tabler/icons-react";
import api from "../../../services/api";

type LandingView = "menu" | "model_select";

const LANDING_VIEW_OPTIONS: { value: LandingView; label: string }[] = [
  { value: "menu", label: "Standard menu" },
  { value: "model_select", label: "AI assistant picker (experimental)" },
];

interface QrCodeRecord {
  id: string;
  restaurantId: string;
  menuId: string | null;
  label: string | null;
  landingView: LandingView;
  image: string | null; // base64 PNG
  createdAt?: string;
}

interface MenuOption {
  id: string;
  name?: string | null;
  adminNotes?: string | null;
  createdAt?: string;
}

interface Props {
  restaurantId: string;
}

/**
 * Admin panel for a restaurant's standalone QR codes.
 *
 * QR codes are permanent records tied to this restaurant: the printed image
 * encodes /m/:<qr id>, so the target menu can be switched at any time without
 * reprinting, and deleting a menu never breaks a code.
 */
export function QrCodePanel({ restaurantId }: Props) {
  const [qrCodes, setQrCodes] = useState<QrCodeRecord[]>([]);
  const [menus, setMenus] = useState<MenuOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newMenuId, setNewMenuId] = useState<string | null>(null);
  const [newLandingView, setNewLandingView] = useState<LandingView>("menu");

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [qrResponse, restaurantResponse] = await Promise.all([
        api.get("/qrcodes", { params: { restaurantId } }),
        api.get(`/restaurants/${restaurantId}`),
      ]);
      setQrCodes(qrResponse.data.qrCodes ?? []);
      setMenus(restaurantResponse.data.restaurant?.menus ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? "Failed to load QR codes");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const menuOptions = menus.map((menu) => {
    const date = menu.createdAt ? new Date(menu.createdAt).toISOString().split("T")[0] : "";
    const label = menu.name || menu.adminNotes || menu.id.slice(0, 8);
    return { value: menu.id, label: date ? `${date} — ${label}` : label };
  });

  const qrUrl = (id: string) => `${window.location.origin}/m/${id}`;

  const handleCreate = async () => {
    try {
      setCreating(true);
      await api.post("/qrcodes", {
        restaurantId,
        menuId: newMenuId,
        label: newLabel.trim() || null,
        landingView: newLandingView,
      });
      setNewLabel("");
      setNewMenuId(null);
      setNewLandingView("menu");
      await fetchData();
      notifications.show({ title: "QR code created", message: "The new QR code is ready to print.", color: "green" });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to create QR code",
        color: "red",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRepoint = async (qr: QrCodeRecord, menuId: string | null) => {
    try {
      setBusyId(qr.id);
      await api.put(`/qrcodes/${qr.id}`, { menuId });
      setQrCodes((prev) => prev.map((item) => (item.id === qr.id ? { ...item, menuId } : item)));
      notifications.show({
        title: "QR code updated",
        message: menuId ? "Printed copies now open the selected menu." : "QR code unassigned from its menu.",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to update QR code",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleSetLandingView = async (qr: QrCodeRecord, landingView: LandingView) => {
    try {
      setBusyId(qr.id);
      await api.put(`/qrcodes/${qr.id}`, { landingView });
      setQrCodes((prev) => prev.map((item) => (item.id === qr.id ? { ...item, landingView } : item)));
      notifications.show({
        title: "QR code updated",
        message:
          landingView === "model_select"
            ? "Scans now open the experimental AI assistant picker."
            : "Scans now open the standard menu.",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to update landing view",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleLabelBlur = async (qr: QrCodeRecord, label: string) => {
    if ((qr.label ?? "") === label.trim()) return;
    try {
      await api.put(`/qrcodes/${qr.id}`, { label: label.trim() || null });
      setQrCodes((prev) =>
        prev.map((item) => (item.id === qr.id ? { ...item, label: label.trim() || null } : item)),
      );
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to update label",
        color: "red",
      });
    }
  };

  const handleRegenerate = async (qr: QrCodeRecord) => {
    try {
      setBusyId(qr.id);
      const response = await api.post(`/qrcodes/${qr.id}/regenerate`);
      const updated = response.data.qrCode as QrCodeRecord;
      setQrCodes((prev) => prev.map((item) => (item.id === qr.id ? { ...item, image: updated.image } : item)));
      notifications.show({
        title: "Image regenerated",
        message: "The encoded link is unchanged, so printed copies keep working.",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to regenerate image",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (qr: QrCodeRecord) => {
    const confirmed = window.confirm(
      "Delete this QR code? Any printed copies of it will permanently stop working.",
    );
    if (!confirmed) return;
    try {
      setBusyId(qr.id);
      await api.delete(`/qrcodes/${qr.id}`);
      setQrCodes((prev) => prev.filter((item) => item.id !== qr.id));
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message ?? "Failed to delete QR code",
        color: "red",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyUrl = async (qr: QrCodeRecord) => {
    try {
      await navigator.clipboard.writeText(qrUrl(qr.id));
      notifications.show({ title: "Link copied", message: qrUrl(qr.id), color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to copy link", color: "red" });
    }
  };

  return (
    <Paper p="md" withBorder>
      <Title order={4} mb="xs">
        QR Codes
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        QR codes belong to this restaurant permanently. Each printed code encodes a stable link, so you can
        switch which menu it opens at any time — no reprinting needed when a menu is replaced.
      </Text>

      {error ? <Alert color="red" mb="md">{error}</Alert> : null}

      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack>
          {qrCodes.length === 0 ? (
            <Text size="sm" c="dimmed">
              No QR codes yet. Create one below.
            </Text>
          ) : (
            <Table verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Points to menu</Table.Th>
                  <Table.Th>Landing view</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {qrCodes.map((qr) => (
                  <Table.Tr key={qr.id}>
                    <Table.Td>
                      <Stack gap={4} align="center">
                        {qr.image ? (
                          <Image
                            src={`data:image/png;base64,${qr.image}`}
                            alt="QR code"
                            w={72}
                            h={72}
                            fit="contain"
                          />
                        ) : (
                          <Text size="xs" c="dimmed">
                            No image
                          </Text>
                        )}
                        <Anchor size="xs" href={qrUrl(qr.id)} target="_blank">
                          /m/{qr.id.slice(0, 8)}…
                        </Anchor>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder="e.g. Table tents"
                        defaultValue={qr.label ?? ""}
                        onBlur={(e) => handleLabelBlur(qr, e.currentTarget.value)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        placeholder="Unassigned"
                        data={menuOptions}
                        value={qr.menuId}
                        onChange={(value) => handleRepoint(qr, value)}
                        disabled={busyId === qr.id}
                        clearable
                      />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={LANDING_VIEW_OPTIONS}
                        value={qr.landingView ?? "menu"}
                        onChange={(value) =>
                          handleSetLandingView(qr, (value as LandingView) || "menu")
                        }
                        disabled={busyId === qr.id}
                        allowDeselect={false}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label="Copy link">
                          <ActionIcon variant="subtle" onClick={() => handleCopyUrl(qr)}>
                            <IconCopy size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Regenerate image (printed copies keep working)">
                          <ActionIcon
                            variant="subtle"
                            loading={busyId === qr.id}
                            onClick={() => handleRegenerate(qr)}
                          >
                            <IconRefresh size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete (printed copies stop working)">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            loading={busyId === qr.id}
                            onClick={() => handleDelete(qr)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Group align="flex-end">
            <TextInput
              size="xs"
              label="Label"
              placeholder="e.g. Front window sticker"
              value={newLabel}
              onChange={(e) => setNewLabel(e.currentTarget.value)}
            />
            <Select
              size="xs"
              label="Points to menu"
              placeholder="Select menu (optional)"
              data={menuOptions}
              value={newMenuId}
              onChange={setNewMenuId}
              clearable
            />
            <Select
              size="xs"
              label="Landing view"
              data={LANDING_VIEW_OPTIONS}
              value={newLandingView}
              onChange={(value) => setNewLandingView((value as LandingView) || "menu")}
              allowDeselect={false}
            />
            <Button size="xs" loading={creating} onClick={handleCreate}>
              New QR code
            </Button>
          </Group>
        </Stack>
      )}
    </Paper>
  );
}
